// src/stores/authStore.ts
import { create } from "zustand";
import { onAuthChange } from "../firebase/auth";
import { getUserDoc } from "../firebase/firestore";
import type { DocumentData } from "firebase/firestore";
import { isRole, type Role } from "../models/auth";
import type { UserProfile } from "../models/user";

type AuthStatus = "idle" | "loading" | "authed" | "error";

interface AuthState {
  status: AuthStatus;
  user: {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
  } | null;
  profile: UserProfile | null;
  error?: string;
}

interface AuthStore {
  state: AuthState;
  start: () => void;
  stop: () => void;
  hasAnyRole: (roles: readonly Role[]) => boolean;
}

let unsubscribe: (() => void) | null = null;

export const useAuthStore = create<AuthStore>((set, get) => ({
  state: { status: "idle", user: null, profile: null },

  start: () => {
    if (unsubscribe) return; // ya suscrito
    set({ state: { status: "loading", user: null, profile: null } });

    unsubscribe = onAuthChange(async (fbUser) => {
      try {
        // No autenticado
        if (!fbUser) {
          set({ state: { status: "idle", user: null, profile: null } });
          return;
        }

        // Usuario base (sin undefined)
        const user = {
          uid: fbUser.uid,
          email: fbUser.email ?? null,
          displayName: fbUser.displayName ?? null,
          photoURL: fbUser.photoURL ?? null,
        };

        // Perfil (si existe doc)
        const snap = await getUserDoc(fbUser.uid);
        const data: DocumentData | undefined = snap.exists()
          ? snap.data()
          : undefined;

        let profile: UserProfile | null = null;
        if (data) {
          // email requerido en UserProfile
          const email: string = String(
            fbUser.email ?? (typeof data.email === "string" ? data.email : "")
          );

          const p: UserProfile = {
            uid: fbUser.uid,
            email,
          };

          if (typeof data.fullName === "string" && data.fullName.length) {
            p.fullName = data.fullName;
          }

          // photoURL puede ser string o null; nunca undefined
          if (typeof data.photoURL === "string" || data.photoURL === null) {
            p.photoURL = data.photoURL;
          } else if (fbUser.photoURL !== undefined) {
            p.photoURL = fbUser.photoURL ?? null;
          }

          if (isRole(data.role)) {
            p.role = data.role;
          }

          // Timestamps si existen en tu modelo
          if (data.createdAt !== undefined) p.createdAt = data.createdAt;
          if (data.updatedAt !== undefined) p.updatedAt = data.updatedAt;

          profile = p;
        }

        set({ state: { status: "authed", user, profile } });
      } catch (err) {
        set({
          state: {
            status: "error",
            user: null,
            profile: null,
            error: (err as Error).message || "Auth error",
          },
        });
      }
    });
  },

  stop: () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    set({ state: { status: "idle", user: null, profile: null } });
  },

  hasAnyRole: (roles) => {
    const role = get().state.profile?.role ?? null;
    return role !== null && roles.includes(role);
  },
}));
